import React, { ReactNode } from 'react';
import Animated, { SharedValue, clamp, useAnimatedStyle, useSharedValue } from 'react-native-reanimated';

import { isWeb } from '@/utils/constants-platform-specific';

/**
 * Returns an animated style that applies a "sticky" translation effect based on scroll position.
 *
 * It applies a translation effect to an element, making it appear to stick to the top of the screen
 *
 * @param {SharedValue<number>} scrollOffset - A Reanimated shared value representing the current scroll offset.
 * @param {SharedValue<number>} initialPosition - A Reanimated shared value representing the element's initial Y position.
 * @returns {AnimatedStyle} An animated style object containing the computed translateY transform and background color.
 */
export const useStickyTranslation = (scrollOffset: SharedValue<number>, initialPosition: SharedValue<number>) => {
    return useAnimatedStyle(() => {
        if (isWeb) {
            return {};
        }
        const translateY = Math.min(0, -scrollOffset.value + initialPosition.value);
        return {
            transform: [
                {
                    translateY: -translateY,
                },
            ],
        };
    }, [scrollOffset, initialPosition]);
};

// velocity threshold to decide if we should show the item or not
const SHOW_VELOCITY_THRESHOLD = 0.3;

/**
 * Custom hook that slides up and down an element based on scroll velocity and direction.
 *
 * This is used when we want to reveal an element when the user scrolls up quickly and hide it when the user scrolls down
 * This hook need to be used with `useStickyTranslation`
 *
 * When the user scrolls up quickly, the item will be translated down to reveal the content.
 * When the user scrolls down, the item will be translated up to hide the content.
 *
 * When the user scrolls up slowly, the item will **not** be translated down
 *
 * @param {SharedValue<number>} scrollOffset - The shared value representing the current scroll offset.
 * @param {SharedValue<number>} scrollVelocity - The shared value representing the current scroll velocity.
 * @param {SharedValue<number>} initialPosition - The shared value representing the initial position of the element.
 * @param {SharedValue<"up" | "down">} scrollDirection - The shared value indicating the current scroll direction ("up" or "down").
 * @param {number} slideAmount - The maximum distance that the element should slide.
 * @returns {AnimatedStyle} - The animated style object containing the translateY transform.
 */
const useSlideVelocityTranslation = (
    scrollOffset: SharedValue<number>,
    scrollVelocity: SharedValue<number>,
    initialPosition: SharedValue<number>,
    scrollDirection: SharedValue<'up' | 'down'>,
    slideAmount: number
) => {
    // whether the item was shown or hidden
    const mode = useSharedValue<'hide' | 'show'>('hide');

    // start fully "hidden" (-slideAmount)
    const baseTranslation = useSharedValue(0);
    const slideTranslation = useSharedValue(0);

    // the offset at which the item was triggered to be shown or hidden - used to calculate the translation
    const triggerOffset = useSharedValue(0);

    // the previous translation value - used in edge cases when we are near the initial position
    const previousTranslation = useSharedValue(0);

    return useAnimatedStyle(() => {
        if (isWeb) {
            return {};
        }
        // extract variables that we only read, for better visibility
        const offset = scrollOffset.value;
        const velocity = scrollVelocity.value;
        const direction = scrollDirection.value;
        const initPos = initialPosition.value;

        // scrolling up, velocity big enough, and the item is hidden
        // means we need to show the item
        if (direction === 'up' && velocity < -SHOW_VELOCITY_THRESHOLD && mode.value === 'hide') {
            // change the mode to show
            mode.value = 'show';
            // save necessary values to compute the translation later if needed
            baseTranslation.value = slideTranslation.value;
            triggerOffset.value = offset;
        }
        // if going down and the item is shown, we need to hide it
        // here we don't need a velocity check since we always hide the item when scrolling down
        else if (direction === 'down' && mode.value === 'show') {
            // change the mode to hide
            mode.value = 'hide';
            // save necessary values to compute the translation later if needed
            baseTranslation.value = slideTranslation.value;
            triggerOffset.value = offset;
        }

        let newSlide;

        // if we are near the initial position there are several edge cases that need to be handled

        // case 1: we are in the interval [initPos - slideAmount, initPos]
        // here the item needs to be translated from it's current position to 0
        // to ensure the item is not translated at all when it reaches the initial position
        // this issue arrives when scrolling up
        if (offset >= initPos - slideAmount && offset <= initPos) {
            if (direction === 'up') {
                newSlide = -(offset - (initPos - slideAmount));
                newSlide = clamp(newSlide, previousTranslation.value, 0);
                slideTranslation.value = newSlide;
            }
        }
        // case 2: we are in the interval [0, initPos - slideAmount]
        // this means we don't need to translate the item at all since he is near the initial position
        // every value -> 0
        else if (offset <= initPos - slideAmount) {
            slideTranslation.value = 0;
            baseTranslation.value = 0;
            triggerOffset.value = 0;
        }
        // case 3: we are in the interval [initPos, Infinity]
        // here we need to translate the item up/down depending using the values we saved before
        else {
            // to the current saved translation, we add how much we have moved from the trigger offset
            newSlide = baseTranslation.value + (offset - triggerOffset.value);

            // we need to clamp the value to ensure we don't go over the boundaries
            newSlide = clamp(newSlide, -slideAmount, 0);

            slideTranslation.value = newSlide;
            previousTranslation.value = newSlide;
        }

        return {
            transform: [{ translateY: -slideTranslation.value }],
        };
    });
};

/**
 * Wrapper component that applies sticky and slide translation effects to its children.
 *
 * This component uses the `useStickyTranslation` and `useSlideVelocityTranslation` hooks to create a sticky effect
 * and slide effect based on the scroll position, velocity, and direction.
 *
 * Created to ease the use of the two hooks together.
 */
export const StickyWrapper = ({
    children,
    scrollOffset,
    initialPosition,
    scrollVelocity,
    scrollDirection,
    slideAmount,
}: {
    children: ReactNode;
    scrollOffset: SharedValue<number>;
    initialPosition: SharedValue<number>;
    scrollVelocity?: SharedValue<number>;
    scrollDirection?: SharedValue<'up' | 'down'>;
    slideAmount?: number;
}) => {
    const stickyStyle = useStickyTranslation(scrollOffset, initialPosition);

    const internalScrollVelocity = useSharedValue(0);
    const internalDirection = useSharedValue<'up' | 'down'>('down');

    const slideStyle = useSlideVelocityTranslation(
        scrollOffset,
        scrollVelocity || internalScrollVelocity,
        initialPosition,
        scrollDirection || internalDirection,
        slideAmount || 0
    );

    return (
        <Animated.View style={stickyStyle}>
            <Animated.View style={slideStyle}>{children}</Animated.View>
        </Animated.View>
    );
};
